import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { addMinutes, differenceInMinutes, format, isToday } from 'date-fns/esm';
import { de } from 'date-fns/locale';

import BookingProjectSettings from '../../../../core/types/model/BookingProjectSettings';

import { RootState } from '../../../../core/state/state';
import { selectFilteredBookingsForDate } from '../../../../core/state/bookings/selectors';
import { selectBlockedSlotsByDate } from '../../../../core/state/blocked-slots/selectors';
import { TimeSlotDefinition } from '../../types';
import { numGridSlots, placeBookings } from '../../helpers';
import { formatDate, getTimeDate } from '../../../../core/utils/date';
import { DATE_FORMAT_HOURS_MINUTES } from '../../../../core/constants/date';

import BookingCard from '../booking-card/BookingCard';
import TimeSlot from '../time-slot/TimeSlot';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';

import './DayColumn.scss';

interface Props {
  date: Date;
  timeSlots: TimeSlotDefinition[];
  projectSettings: BookingProjectSettings;
  isPrinting?: boolean;
}

const DayColumn: React.FC<Props> = ({ date, timeSlots, projectSettings, isPrinting }) => {
  const bookings = useSelector((state: RootState) => selectFilteredBookingsForDate(state, date));
  const blockedSlots = useSelector((state: RootState) => selectBlockedSlotsByDate(state, date));
  const language = useSelector((state: RootState) => state.user.userInfo?.lang);
  const isMobileScreen = isMobileScreenSize();

  const {
    bookingProjectSettings: { operatingHoursFrom, operatingHoursTo }
  } = projectSettings;

  const placement = useMemo(
    () => (bookings.length ? placeBookings(bookings, operatingHoursFrom, operatingHoursTo) : { layouts: [], overflowRows: [] }),
    [bookings, operatingHoursFrom, operatingHoursTo]
  );
  const { layouts: placedBookings, overflowRows } = placement;

  const isSlotBlocked = (timeSlot: TimeSlotDefinition): boolean => {
    if (blockedSlots.length > 0 && projectSettings.projectDeliveryZones) {
      return blockedSlots
        .map((blockedSlot) => {
          const blockedSlotStart = blockedSlot?.timeSlot.split('-')[0];
          const blockedSlotEnd = blockedSlot?.timeSlot.split('-')[1];
          const durationMinutes = differenceInMinutes(getTimeDate(blockedSlotEnd), getTimeDate(blockedSlotStart));
          if (durationMinutes < 30) {
            return false;
          }

          if (
            blockedSlot?.blockedAllDay === true &&
            projectSettings.projectDeliveryZones.length === blockedSlot.zones.filter((f) => f.isActive === true).length
          ) {
            return true;
          }

          if (
            formatDate(addMinutes(getTimeDate(timeSlot.startTime), 30), DATE_FORMAT_HOURS_MINUTES) >= blockedSlotStart &&
            formatDate(addMinutes(getTimeDate(timeSlot.startTime), 30), DATE_FORMAT_HOURS_MINUTES) <= blockedSlotEnd &&
            projectSettings.projectDeliveryZones.length === blockedSlot.zones.filter((f) => f.isActive === true).length
          ) {
            return true;
          }
          return false;
        })
        .includes(true);
    }
    return false;
  };

  const isPartiallyBlocked = (timeSlot: TimeSlotDefinition): string => {
    if (!(blockedSlots.length > 0 && projectSettings.projectDeliveryZones)) {
      return 'none';
    }

    const timeSlotStart = timeSlot.startTime.slice(0, 2);

    const matchedSlot = blockedSlots.find(
      (item) => item.timeSlot.split('-')[0].slice(0, 2) === timeSlotStart || item.timeSlot.split('-')[1].slice(0, 2) === timeSlotStart
    );

    if (matchedSlot !== undefined) {
      const matchedSlotStart = matchedSlot?.timeSlot.split('-')[0];
      const matchedSlotEnd = matchedSlot?.timeSlot.split('-')[1];
      const durationMinutes = differenceInMinutes(getTimeDate(matchedSlotEnd), getTimeDate(matchedSlotStart));

      if (durationMinutes < 30) {
        return 'none';
      }

      if (
        matchedSlotStart.split(':')[1] === '30' &&
        matchedSlotStart > timeSlot.startTime &&
        matchedSlotStart < formatDate(addMinutes(getTimeDate(timeSlot.startTime), 60), 'HH:MM')
      ) {
        return 'bottom';
      }
      if (
        matchedSlotEnd.split(':')[1] === '30' &&
        matchedSlotEnd > timeSlot.startTime &&
        matchedSlotEnd < formatDate(addMinutes(getTimeDate(timeSlot.startTime), 60), 'HH:MM')
      ) {
        return 'top';
      }
    }
    return 'none';
  };

  const gridTemplateRowsStyle = `repeat(${numGridSlots(operatingHoursFrom, operatingHoursTo)}, var(--calendar-grid-row-height)`;

  return (
    <div className={`column ${isMobileScreen && 'column--mobile'}`}>
      <div className={`column__header ${isToday(date) ? 'column__header--today' : ''}`}>
        {!isMobileScreen && format(date, 'EEE d', language === 'de-DE' ? { locale: de } : undefined)}
      </div>
      <div className="column__content" style={{ gridTemplateRows: gridTemplateRowsStyle }}>
        {timeSlots.map((config) => (
          <TimeSlot
            key={config.index}
            date={date}
            config={config}
            isBlocked={isSlotBlocked(config)}
            isPartiallyBlocked={isPartiallyBlocked(config)}
            overflowRows={overflowRows}
          />
        ))}

        {placedBookings.map((bookingLayout) => (
          <BookingCard key={bookingLayout.booking.id} bookingLayout={bookingLayout} isPrinting={isPrinting} />
        ))}
      </div>
    </div>
  );
};

export default DayColumn;
